import { z } from 'zod';

/**
 * Money arithmetic in whole cents (ADR 0006): amounts travel as 2-decimal strings, sums are
 * exact integers, so totals never drift by floating-point error. Divisions round half up.
 */
export type Cents = number;

export const toCents = (value: string | number | null | undefined): Cents => {
  if (value === null || value === undefined || value === '') return 0;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
};

export const fromCents = (cents: Cents): string => (cents / 100).toFixed(2);

export const sumCents = (values: readonly Cents[]): Cents => values.reduce((s, v) => s + v, 0);

/** Percentage `part / whole × 100`, rounded; 0 when `whole` is 0. */
export const percentOf = (part: number, whole: number): number =>
  whole === 0 ? 0 : Math.round((part / whole) * 100);

/** A non-negative amount typed into a sheet ("1 234,5" → "1234.50"). */
export const amountInSchema = z
  .union([z.number(), z.string()])
  .transform((v) => (typeof v === 'number' ? String(v) : v.trim().replace(/\s/g, '').replace(',', '.')))
  .transform((v) => (v === '' ? '0' : v))
  .pipe(
    z
      .string()
      .regex(/^\d{1,12}(\.\d{1,2})?$/, { message: 'Nederīga summa' })
      .transform((v) => Number(v).toFixed(2)),
  );
